/**
 * Formatted email for the reply composer: the one sanitizer, and the plain text
 * that goes beside it.
 *
 * `sanitizeRichText` is the only way HTML written in the composer reaches a
 * stored draft, a template, a sent email or the page. The server runs it on
 * every `html` it is handed, and the web runs this same file (imported as
 * `@gojibasel/shared/rich-text`) on load, on paste and when leaving the source
 * view. It has NO IMPORTS on purpose: the browser bundle takes this file as it
 * is, so an import here would drag server code into the page. There is no HTML
 * library in the workspace, so the tokenizer lives here too.
 *
 * Why a hand-written tokenizer is safe: the output is never the input with the
 * bad parts cut out. It is written fresh from a small tree of escaped text and
 * allowlisted elements, each carrying only allowlisted attributes, every value
 * double-quoted and escaped. Where this tokenizer reads a malformed input
 * differently from a browser, some text is kept or lost, but no markup gets
 * through, because a browser reading the OUTPUT sees exactly the tree written.
 * The same reason makes it idempotent: the output is already in the one shape
 * the writer produces, so a second pass reads it back to the same tree.
 *
 * `richTextToPlain` is the text part of the email and the `body` a formatted
 * reply is stored under: line breaks for `br`, block ends and list items, a
 * link as `text (url)`, entities decoded.
 */

type Attr = readonly [name: string, value: string]

interface StartTag {
  readonly kind: 'start'
  readonly name: string
  readonly attrs: readonly Attr[]
  readonly selfClosing: boolean
}
interface EndTag {
  readonly kind: 'end'
  readonly name: string
}
interface TextToken {
  readonly kind: 'text'
  readonly text: string
}
type Token = StartTag | EndTag | TextToken

interface ElementNode {
  readonly type: 'element'
  readonly tag: string
  readonly attrs: readonly Attr[]
  readonly children: TreeNode[]
}
interface TextNode {
  readonly type: 'text'
  text: string
}
type TreeNode = ElementNode | TextNode

/** The tags that survive, exactly the contract's list. */
const ALLOWED = new Set([
  'p',
  'div',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'a',
  'img',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
])
const VOID = new Set(['br', 'img'])

/**
 * Tags that go WITH everything inside them. The contract's list, plus `title`:
 * a whole pasted page carries its title in `<head>`, which a browser never shows,
 * and keeping it would put the page's name at the top of the message.
 */
const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'template',
  'noscript',
  'textarea',
  'select',
  'title',
])

/**
 * Block tags outside the allowlist become a plain `div`, which is on it. A pasted
 * heading or table row otherwise loses its line, and "Pricing" runs into the
 * sentence under it as "PricingOur plans start". The text is kept either way,
 * which is all the contract asks of a disallowed tag; this keeps its line too.
 */
const AS_DIV = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'section',
  'article',
  'header',
  'footer',
  'nav',
  'aside',
  'main',
  'figure',
  'figcaption',
  'address',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'dl',
  'dt',
  'dd',
  'center',
  'details',
  'summary',
  'fieldset',
  'form',
  'legend',
  'hgroup',
])

/**
 * Elements whose content a browser reads as text rather than as tags, so a `<b>`
 * inside a `<script>` is not a tag. RCDATA ones still decode entities.
 */
const RAW_TEXT = new Set([
  'script',
  'style',
  'iframe',
  'noscript',
  'noembed',
  'noframes',
  'xmp',
  'textarea',
  'title',
  'plaintext',
])
const RCDATA = new Set(['textarea', 'title'])

/** Opening one of these closes an open `p`, as a browser does. */
const CLOSES_P = new Set(['p', 'div', 'ul', 'ol', 'blockquote', 'pre', 'li'])

/**
 * How deep elements may nest. Deeper start tags are dropped (their text stays),
 * so ten thousand nested `<b>` cannot overflow the stack of whoever renders the
 * tree, and the output never nests deeper than this, so a second pass keeps it.
 */
const MAX_DEPTH = 100

const ALIGNMENTS = new Set(['left', 'center', 'right', 'justify'])
const HREF_SCHEMES = /^(?:https?|mailto):/i
const SRC_SCHEMES = /^https?:/i

// ---------------------------------------------------------------------------
// Characters and entities

function isWhitespace(code: number): boolean {
  return code === 9 || code === 10 || code === 12 || code === 13 || code === 32
}

function isAsciiAlpha(code: number): boolean {
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122)
}

/** ASCII only, as an HTML parser lowercases a tag name. */
function asciiLower(s: string): string {
  return s.replace(/[A-Z]+/g, (m) => m.toLowerCase())
}

/** Latin-1's named entities, U+00A0 to U+00FF in order. */
const LATIN1 =
  'nbsp iexcl cent pound curren yen brvbar sect uml copy ordf laquo not shy reg macr deg plusmn ' +
  'sup2 sup3 acute micro para middot cedil sup1 ordm raquo frac14 frac12 frac34 iquest Agrave ' +
  'Aacute Acirc Atilde Auml Aring AElig Ccedil Egrave Eacute Ecirc Euml Igrave Iacute Icirc Iuml ' +
  'ETH Ntilde Ograve Oacute Ocirc Otilde Ouml times Oslash Ugrave Uacute Ucirc Uuml Yacute THORN ' +
  'szlig agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml igrave iacute ' +
  'icirc iuml eth ntilde ograve oacute ocirc otilde ouml divide oslash ugrave uacute ucirc uuml ' +
  'yacute thorn yuml'

const NAMED_ENTITIES: Record<string, string> = (() => {
  const table: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    OElig: 'Œ',
    oelig: 'œ',
    Scaron: 'Š',
    scaron: 'š',
    Yuml: 'Ÿ',
    fnof: 'ƒ',
    circ: 'ˆ',
    tilde: '˜',
    ensp: ' ',
    emsp: ' ',
    thinsp: ' ',
    zwnj: '‌',
    zwj: '‍',
    lrm: '‎',
    rlm: '‏',
    ndash: '–',
    mdash: '—',
    lsquo: '‘',
    rsquo: '’',
    sbquo: '‚',
    ldquo: '“',
    rdquo: '”',
    bdquo: '„',
    dagger: '†',
    Dagger: '‡',
    bull: '•',
    hellip: '…',
    permil: '‰',
    prime: '′',
    Prime: '″',
    lsaquo: '‹',
    rsaquo: '›',
    euro: '€',
    trade: '™',
    larr: '←',
    uarr: '↑',
    rarr: '→',
    darr: '↓',
    harr: '↔',
    check: '✓',
    hearts: '♥',
  }
  LATIN1.split(' ').forEach((name, index) => {
    table[name] = String.fromCharCode(0xa0 + index)
  })
  return table
})()

/**
 * One left-to-right pass, never repeated, so `&amp;lt;` decodes to the text
 * `&lt;` and not to `<`. Numeric references may drop the semicolon, as browsers
 * allow. A named one needs it, and an unknown name stays as it was written.
 */
function decodeEntities(s: string): string {
  if (s.indexOf('&') === -1) return s
  return s.replace(
    /&(?:#(\d{1,10});?|#[xX]([0-9a-fA-F]{1,8});?|([a-zA-Z][a-zA-Z0-9]{0,31});)/g,
    (match: string, dec?: string, hex?: string, name?: string) => {
      if (name !== undefined) return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name]! : match
      const code = dec !== undefined ? Number.parseInt(dec, 10) : Number.parseInt(hex ?? '', 16)
      if (!Number.isFinite(code) || code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return '�'
      return String.fromCodePoint(code)
    },
  )
}

/**
 * Line endings as a browser normalizes them, and no control characters except
 * the tab and the newline. A decoded `&#13;` could otherwise put a carriage
 * return in the output that the next pass would turn into a newline.
 */
function cleanText(s: string): string {
  return s.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '')
}

function escapeText(s: string): string {
  return s.replace(/[&<> ]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&nbsp;'))
}

function escapeAttribute(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'))
}

/**
 * A URL as a browser reads it out of an attribute: tabs and newlines anywhere
 * are ignored and so are control characters and spaces at either end. That is
 * how `java&#9;script:` and ` javascript:` turn into `javascript:` in a browser,
 * so the scheme is checked on THIS form, and this form is what gets written.
 */
function cleanUrl(value: string): string {
  return cleanText(value).replace(/[\t\n\r]/g, '').replace(/^[\u0000- ]+|[\u0000- ]+$/g, '')
}

// ---------------------------------------------------------------------------
// Tokenizer

interface ReadTag {
  readonly name: string
  readonly attrs: Attr[]
  readonly selfClosing: boolean
  readonly end: number
}

/**
 * Reads a tag from its name to its `>`, the way a browser's tokenizer does:
 * quoted values may hold `>`, a duplicated attribute keeps its first value, and
 * a tag the input ends inside is no tag at all (null).
 */
function readTag(s: string, start: number): ReadTag | null {
  const n = s.length
  let i = start
  while (i < n) {
    const code = s.charCodeAt(i)
    if (isWhitespace(code) || code === 47 /* / */ || code === 62 /* > */) break
    i++
  }
  const name = asciiLower(s.slice(start, i))
  const attrs: Attr[] = []
  const seen = new Set<string>()
  for (;;) {
    while (i < n && isWhitespace(s.charCodeAt(i))) i++
    if (i >= n) return null
    const code = s.charCodeAt(i)
    if (code === 62 /* > */) return { name, attrs, selfClosing: false, end: i + 1 }
    if (code === 47 /* / */) {
      i++
      if (i < n && s.charCodeAt(i) === 62) return { name, attrs, selfClosing: true, end: i + 1 }
      continue
    }
    // An attribute name. Its first character may be anything, `=` included.
    const nameStart = i
    i++
    while (i < n) {
      const c = s.charCodeAt(i)
      if (isWhitespace(c) || c === 47 || c === 62 || c === 61 /* = */) break
      i++
    }
    const attrName = asciiLower(s.slice(nameStart, i))
    while (i < n && isWhitespace(s.charCodeAt(i))) i++
    let value = ''
    if (i < n && s.charCodeAt(i) === 61 /* = */) {
      i++
      while (i < n && isWhitespace(s.charCodeAt(i))) i++
      if (i >= n) return null
      const quote = s.charCodeAt(i)
      if (quote === 34 /* " */ || quote === 39 /* ' */) {
        const close = s.indexOf(quote === 34 ? '"' : "'", i + 1)
        if (close === -1) return null
        value = s.slice(i + 1, close)
        i = close + 1
      } else if (quote !== 62) {
        const valueStart = i
        while (i < n) {
          const c = s.charCodeAt(i)
          if (isWhitespace(c) || c === 62) break
          i++
        }
        value = s.slice(valueStart, i)
      }
    }
    if (!seen.has(attrName)) {
      seen.add(attrName)
      attrs.push([attrName, decodeEntities(value)])
    }
  }
}

/** Where a raw-text element's content ends: its own end tag, or the input's end. */
function rawTextEnd(s: string, from: number, name: string): number {
  // `name` comes from the fixed RAW_TEXT set, never from the input.
  const pattern = new RegExp(`</${name}[\\t\\n\\f\\r />]`, 'gi')
  pattern.lastIndex = from
  const match = pattern.exec(s)
  return match ? match.index : s.length
}

/** Where a comment that opened at `from` (just past `<!--`) ends. */
function commentEnd(s: string, from: number): number {
  if (s.startsWith('>', from)) return from + 1
  if (s.startsWith('->', from)) return from + 2
  const plain = s.indexOf('-->', from)
  const bang = s.indexOf('--!>', from)
  if (plain === -1 && bang === -1) return s.length
  if (plain !== -1 && (bang === -1 || plain < bang)) return plain + 3
  return bang + 4
}

function tokenize(input: string): Token[] {
  const s = input.replace(/\r\n?/g, '\n')
  const n = s.length
  const tokens: Token[] = []
  let textStart = 0
  let i = 0
  const flushText = (end: number): void => {
    if (end > textStart) tokens.push({ kind: 'text', text: decodeEntities(s.slice(textStart, end)) })
  }
  const skipTo = (next: number): void => {
    i = next
    textStart = next
  }

  while (i < n) {
    const lt = s.indexOf('<', i)
    if (lt === -1) break
    const next = s.charCodeAt(lt + 1)

    if (isAsciiAlpha(next)) {
      flushText(lt)
      const tag = readTag(s, lt + 1)
      if (tag === null) {
        skipTo(n)
        break
      }
      tokens.push({ kind: 'start', name: tag.name, attrs: tag.attrs, selfClosing: tag.selfClosing })
      skipTo(tag.end)
      if (RAW_TEXT.has(tag.name)) {
        const end = tag.name === 'plaintext' ? n : rawTextEnd(s, i, tag.name)
        const raw = s.slice(i, end)
        if (raw) tokens.push({ kind: 'text', text: RCDATA.has(tag.name) ? decodeEntities(raw) : raw })
        skipTo(end)
      }
      continue
    }

    if (next === 47 /* / */) {
      const after = s.charCodeAt(lt + 2)
      if (isAsciiAlpha(after)) {
        flushText(lt)
        const tag = readTag(s, lt + 2)
        if (tag === null) {
          skipTo(n)
          break
        }
        tokens.push({ kind: 'end', name: tag.name })
        skipTo(tag.end)
        continue
      }
      if (after === 62 /* > */) {
        // `</>` is nothing at all
        flushText(lt)
        skipTo(lt + 3)
        continue
      }
      if (Number.isNaN(after)) {
        // `</` at the very end is text
        i = n
        break
      }
      // `</ x>`, `</3>`: a bogus comment up to the next `>`
      flushText(lt)
      const gt = s.indexOf('>', lt + 2)
      skipTo(gt === -1 ? n : gt + 1)
      continue
    }

    if (next === 33 /* ! */) {
      flushText(lt)
      if (s.startsWith('<!--', lt)) skipTo(commentEnd(s, lt + 4))
      else {
        // `<!DOCTYPE>`, `<![CDATA[`, `<!x`: up to the next `>`
        const gt = s.indexOf('>', lt + 2)
        skipTo(gt === -1 ? n : gt + 1)
      }
      continue
    }

    if (next === 63 /* ? */) {
      flushText(lt)
      const gt = s.indexOf('>', lt + 2)
      skipTo(gt === -1 ? n : gt + 1)
      continue
    }

    // A `<` that opens nothing (`a < b`, `<3`) is text.
    i = lt + 1
  }
  flushText(n)
  return tokens
}

// ---------------------------------------------------------------------------
// The tree: allowlisted elements and attributes, balanced

function attributeOf(attrs: readonly Attr[], name: string): string | undefined {
  for (const [key, value] of attrs) if (key === name) return value
  return undefined
}

function textAlignOf(style: string): string | null {
  let found: string | null = null
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':')
    if (colon === -1) continue
    const property = declaration.slice(0, colon).trim().toLowerCase()
    const value = declaration.slice(colon + 1).trim().toLowerCase()
    // The last valid declaration wins, as in CSS.
    if (property === 'text-align' && ALIGNMENTS.has(value)) found = value
  }
  return found
}

/**
 * The attributes a kept element keeps, in a fixed order so a second pass writes
 * them the same way. Null means drop the element: an image with nothing safe to
 * show.
 */
function allowedAttributes(tag: string, attrs: readonly Attr[]): Attr[] | null {
  if (tag === 'a') {
    const href = attributeOf(attrs, 'href')
    if (href === undefined) return []
    const url = cleanUrl(href)
    return HREF_SCHEMES.test(url) ? [['href', url]] : []
  }
  if (tag === 'img') {
    const src = attributeOf(attrs, 'src')
    if (src === undefined) return null
    const url = cleanUrl(src)
    if (!SRC_SCHEMES.test(url)) return null
    const kept: Attr[] = [['src', url]]
    const alt = attributeOf(attrs, 'alt')
    if (alt !== undefined) kept.push(['alt', cleanText(alt)])
    for (const dimension of ['width', 'height']) {
      const value = attributeOf(attrs, dimension)?.trim()
      if (value !== undefined && /^\d{1,5}$/.test(value)) kept.push([dimension, value])
    }
    return kept
  }
  if (tag === 'p' || tag === 'div') {
    const style = attributeOf(attrs, 'style')
    const align = style === undefined ? null : textAlignOf(style)
    return align === null ? [] : [['style', `text-align: ${align}`]]
  }
  return []
}

function appendText(list: TreeNode[], raw: string): void {
  const text = cleanText(raw)
  if (!text) return
  const last = list[list.length - 1]
  if (last !== undefined && last.type === 'text') last.text += text
  else list.push({ type: 'text', text })
}

interface OpenElement {
  readonly tag: string
  /** The name in the input, so `</table>` closes the div a table became. */
  readonly source: string
  readonly children: TreeNode[]
}

function buildTree(html: string): TreeNode[] {
  const root: TreeNode[] = []
  const stack: OpenElement[] = []
  const current = (): TreeNode[] => (stack.length === 0 ? root : stack[stack.length - 1]!.children)
  const topmost = (match: (open: OpenElement) => boolean): number => {
    for (let k = stack.length - 1; k >= 0; k--) if (match(stack[k]!)) return k
    return -1
  }
  const closeFrom = (index: number): void => {
    if (index !== -1) stack.length = index
  }
  let dropping: { name: string; depth: number } | null = null

  for (const token of tokenize(typeof html === 'string' ? html : '')) {
    // A self-closed `<svg/>` or `<math/>` holds nothing, as in a browser.
    const selfClosedForeign = token.kind === 'start' && token.selfClosing && (token.name === 'svg' || token.name === 'math')

    if (dropping !== null) {
      if (token.kind === 'start' && token.name === dropping.name && !selfClosedForeign) dropping.depth++
      else if (token.kind === 'end' && token.name === dropping.name && --dropping.depth === 0) dropping = null
      continue
    }

    if (token.kind === 'text') {
      appendText(current(), token.text)
      continue
    }

    if (token.kind === 'end') {
      closeFrom(topmost((open) => open.source === token.name))
      continue
    }

    if (DROP_WITH_CONTENT.has(token.name)) {
      // `embed` is void: nothing to drop but itself.
      if (token.name !== 'embed' && !selfClosedForeign) dropping = { name: token.name, depth: 1 }
      continue
    }

    const tag = ALLOWED.has(token.name) ? token.name : AS_DIV.has(token.name) ? 'div' : null
    if (tag === null) continue
    const attrs = allowedAttributes(tag, token.attrs)
    if (attrs === null) continue
    if (VOID.has(tag)) {
      current().push({ type: 'element', tag, attrs, children: [] })
      continue
    }

    // The end tags a browser implies, so the output nests the way it renders.
    if (CLOSES_P.has(tag)) closeFrom(topmost((open) => open.tag === 'p'))
    if (tag === 'li') {
      for (let k = stack.length - 1; k >= 0; k--) {
        const open = stack[k]!
        if (open.tag === 'li') {
          closeFrom(k)
          break
        }
        if (open.tag === 'ul' || open.tag === 'ol' || open.tag === 'blockquote' || open.tag === 'pre') break
      }
    }
    if (tag === 'a') closeFrom(topmost((open) => open.tag === 'a'))

    if (stack.length >= MAX_DEPTH) continue
    const node: ElementNode = { type: 'element', tag, attrs, children: [] }
    current().push(node)
    stack.push({ tag, source: token.name, children: node.children })
  }
  return root
}

function serialize(nodes: readonly TreeNode[]): string {
  let out = ''
  for (const node of nodes) {
    if (node.type === 'text') {
      out += escapeText(node.text)
      continue
    }
    out += `<${node.tag}`
    for (const [name, value] of node.attrs) out += ` ${name}="${escapeAttribute(value)}"`
    out += '>'
    if (!VOID.has(node.tag)) out += `${serialize(node.children)}</${node.tag}>`
  }
  return out
}

/**
 * The allowlist sanitizer. Tags: p div br strong b em i u a img ul ol li
 * blockquote pre code. Attributes: a[href] on http, https or mailto, img[src]
 * on http or https, img[alt], img[width] and img[height] as digits, and a
 * text-align style on p and div. Everything else goes: a dropped tag keeps its
 * text, except script, style, iframe, object, embed, svg, math, template,
 * noscript, textarea, select and title, which take their content with them.
 * Comments go. Unclosed tags are closed and stray end tags dropped. Idempotent.
 */
export function sanitizeRichText(html: string): string {
  return serialize(buildTree(html))
}

/**
 * Plain text as HTML that `sanitizeRichText` leaves exactly as it is: escaped,
 * with a `br` for each line break. For quoting a stored plain message inside a
 * formatted reply.
 */
export function plainToRichText(text: string): string {
  const clean = cleanText(typeof text === 'string' ? text : '')
  return clean
    .split('\n')
    .map((line) => escapeText(line))
    .join('<br>')
}

// ---------------------------------------------------------------------------
// Plain text

interface ListFrame {
  readonly ordered: boolean
  count: number
}
interface PlainContext {
  readonly pre: boolean
  readonly lists: readonly ListFrame[]
}

function trimEndSpaces(s: string): string {
  let end = s.length
  while (end > 0 && s.charCodeAt(end - 1) === 32) end--
  return end === s.length ? s : s.slice(0, end)
}

function sameTarget(label: string, href: string): boolean {
  const norm = (s: string): string => s.trim().toLowerCase().replace(/\/+$/, '')
  const text = norm(label)
  const url = norm(href)
  return text === url || `mailto:${text}` === url || `https://${text}` === url || `http://${text}` === url
}

/**
 * Writes text the way a reader sees the page: whitespace collapsed outside
 * `pre`, a line for each block, a blank line around a paragraph, and a break
 * asked for at the very start or end never written.
 */
class PlainWriter {
  private out = ''
  /** Newlines the next words need in front of them (1 a line, 2 a blank line). */
  private pending = 0
  /** A list item's marker, written with the item's first words. */
  private marker: string | null = null

  write(nodes: readonly TreeNode[], context: PlainContext): void {
    for (const node of nodes) this.node(node, context)
  }

  done(): string {
    const lines = this.out.split('\n').map(trimEndSpaces)
    let start = 0
    let end = lines.length
    while (start < end && lines[start] === '') start++
    while (end > start && lines[end - 1] === '') end--
    return lines.slice(start, end).join('\n')
  }

  private node(node: TreeNode, context: PlainContext): void {
    if (node.type === 'text') {
      this.text(node.text, context.pre)
      return
    }
    const { tag, children } = node
    switch (tag) {
      case 'br':
        this.flush()
        this.out = trimEndSpaces(this.out) + '\n'
        return
      case 'img': {
        const alt = attributeOf(node.attrs, 'alt')?.trim()
        if (alt) this.text(`[${alt}]`, false)
        return
      }
      case 'a':
        this.link(node, context)
        return
      case 'p':
        this.block(2)
        this.write(children, context)
        this.block(2)
        return
      case 'div':
        this.block(1)
        this.write(children, context)
        this.block(1)
        return
      case 'pre': {
        // A browser drops the one newline right after `<pre>`.
        const first = children[0]
        const kids: readonly TreeNode[] =
          first !== undefined && first.type === 'text' && first.text.startsWith('\n')
            ? [{ type: 'text', text: first.text.slice(1) }, ...children.slice(1)]
            : children
        this.block(1)
        this.write(kids, { ...context, pre: true })
        this.block(1)
        return
      }
      case 'ul':
      case 'ol':
        this.block(1)
        this.write(children, { ...context, lists: [...context.lists, { ordered: tag === 'ol', count: 0 }] })
        this.block(1)
        return
      case 'li': {
        this.block(1)
        const list = context.lists[context.lists.length - 1]
        const indent = '  '.repeat(Math.max(0, context.lists.length - 1))
        this.marker = indent + (list?.ordered === true ? `${++list.count}. ` : '- ')
        this.write(children, context)
        this.marker = null
        this.block(1)
        return
      }
      case 'blockquote': {
        const inner = new PlainWriter()
        inner.write(children, { pre: context.pre, lists: [] })
        const quoted = inner.done()
        if (!quoted) return
        this.block(1)
        this.flush()
        this.out += quoted
          .split('\n')
          .map((line) => (line ? `> ${line}` : '>'))
          .join('\n')
        this.block(1)
        return
      }
      default:
        // strong, b, em, i, u, code: the words as they are
        this.write(children, context)
    }
  }

  private link(node: ElementNode, context: PlainContext): void {
    this.write(node.children, context)
    const href = attributeOf(node.attrs, 'href')
    if (!href) return
    const label = new PlainWriter()
    label.write(node.children, { pre: false, lists: [] })
    const words = label.done()
    if (words && sameTarget(words, href)) return
    this.text(words ? ` (${href})` : href, false)
  }

  private block(newlines: number): void {
    // The item's own first block adds no line between the marker and its words.
    if (this.marker !== null) return
    if (this.out !== '') this.pending = Math.max(this.pending, newlines)
  }

  private text(raw: string, pre: boolean): void {
    if (pre) {
      if (!raw) return
      this.flush()
      this.out += raw.replace(/ /g, ' ')
      return
    }
    let words = raw.replace(/[\t\n\f\r ]+/g, ' ')
    if (
      words.startsWith(' ') &&
      (this.pending > 0 || this.marker !== null || this.out === '' || this.out.endsWith('\n') || this.out.endsWith(' '))
    ) {
      words = words.slice(1)
    }
    if (!words) return
    this.flush()
    this.out += words.replace(/ /g, ' ')
  }

  private flush(): void {
    if (this.pending > 0) {
      this.out = trimEndSpaces(this.out)
      let have = 0
      for (let k = this.out.length - 1; k >= 0 && this.out.charCodeAt(k) === 10; k--) have++
      if (this.pending > have) this.out += '\n'.repeat(this.pending - have)
      this.pending = 0
    }
    if (this.marker !== null) {
      this.out += this.marker
      this.marker = null
    }
  }
}

/**
 * The text alternative of formatted HTML, and the body a formatted message is
 * stored and searched under. It sanitizes first, so it reads only what
 * `sanitizeRichText` would keep.
 */
export function richTextToPlain(html: string): string {
  const writer = new PlainWriter()
  writer.write(buildTree(html), { pre: false, lists: [] })
  return writer.done()
}
